import { TypeUnitValue } from '@helptapp/neris-client/models';

import { AgencyType } from './agency';
import { IAuditModel, ObjectId } from './base';
import { IUser } from './user';

export enum UnitStatus {
  ON_DUTY = 'ON_DUTY',
  OFF_DUTY = 'OFF_DUTY',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
}

export enum EquipmentType {
  RADIO = 'RADIO',
  GUN = 'GUN',
  GPS_TRACKER = 'GPS_TRACKER',
}

export enum AssignedUnitShiftStatus {
  ASSIGNED = 'ASSIGNED',
  EN_ROUTE = 'EN_ROUTE',
  ON_SCENE = 'ON_SCENE',
  PROGRESS = 'PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  QUEUED = 'QUEUED',
  IN_THE_AREA = 'IN_THE_AREA',
}

export enum AssignedUnitShiftFriendlyStatus {
  ASSIGNED = 'Assigned',
  EN_ROUTE = 'En Route',
  ON_SCENE = 'On Scene',
  PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  CANCELED = 'Canceled',
  QUEUED = 'Queued',
  IN_THE_AREA = 'In The Area',
}

export enum ResourceType {
  VEHICLE = 'VEHICLE',
  PEOPLE = 'PEOPLE',
  EQUIPMENT = 'EQUIPMENT',
  POLYGON_LAYER = 'POLYGON_LAYER',
  LINE_LAYER = 'LINE_LAYER',
  POINT_LAYER = 'POINT_LAYER',
}

export interface IResource {
  type: ResourceType;
  subType?: EquipmentType | null;
  min: number;
  max?: number;
  isDefault: boolean;
}

export interface IUnitNerisType {
  /**
   * The unit's NERIS ID
   *
   * Example:
   * FD24027334S001U003
   * FD24160543S010U001
   */
  unitNerisId?: string | null;
  unitNerisType?: TypeUnitValue;
  usedInIncidents?: boolean;
}

export interface IUnitType extends ObjectId {
  name: string;
  icon?: string;
  agencyType: AgencyType;
  resources: IResource[];
  isDispatchToHQEnabled?: boolean;
  closeUnitShiftWithCFS?: boolean;
  deleted?: boolean;
  agency?: string;
  hasAssociatedUnits?: boolean;
  hasOnDutyUnits?: boolean;
}

export interface IUnitFilterForm {
  _id?: string;
  name: string;
  agencies?: string[] | null;
  agencyType?: AgencyType | null;
  beat?: string | null;
  unitTypes?: string[] | null;
}

export interface FilterValues {
  agency?: string | null;
  agencyType?: AgencyType | null;
  beat?: string | null;
  unitTypes?: string[] | null;
}

export interface IUnitFilter
  extends Omit<IUnitFilterForm, '_id'>,
    IAuditModel {}

export interface IUnitResourceForm {
  vehicles?: string[];
  users?: string[] | IUser[];
  equipments?: string[];
  polygons?: string[];
  lines?: string[];
  points?: string[];
}

export interface IUnitShiftForm extends IUnitResourceForm {
  unit: string;
  startAt?: string;
  notes?: string;
  wasOnDutiedFromCFS?: boolean;
}

export interface IUnitNerisForm {
  /** NERIS unit ID, can be assigned if there is unassigned NERIS units. */
  unitNerisId?: string;
  // NERIS unit type, requred field when creating/updating a unit in NERIS.
  unitNerisType?: string;
  /** This prop is use for edit unit. Can't be updated or set in create new unit form (it's false by default) or by editing unit. This is read only to check if NERIS unit can be edited or not. */
  usedInIncidents?: boolean;
  /** When creating a new unit, user can check to sync the unit with a NERIS unit. Unit will be created in NERIS and assigned to the new unit.**/
  autoCreateAndAssignNerisUnit?: boolean | undefined;
  /** When creating a new unit, user must specify the minimum staffing required for the unit to be dispatched to an incident. This is requred by NERIS.*/
  /** NOTE: This field is only visible when the unit type has no people resources assigned. */
  stuffing?: number | undefined;
}

export interface IUnitForm extends ObjectId, IUnitResourceForm, IUnitNerisForm {
  name: string;
  type: string;
  status?: UnitStatus;
  notes?: string;
  isStandByWhenOffDuty: boolean;
}
export interface IUnit extends IAuditModel, Omit<IUnitForm, 'type'> {
  _id: string;
  agency: string;
  type: IUnitType;
  status: UnitStatus;
  beat?: string;
  callSign?: string;
  /**
   * Only for Fire RMS
   * This is used to store the unit's NERIS ID and type.
   */
  neris?: IUnitNerisType;
}
